import { useState } from 'react';
import type { Flavour, Product } from '../domain/models';
import { useTranslation } from 'src/lib/i18n/useTranslation';
import { ZoomOverlay } from 'src/lib/common/ui/ZoomOverlay';
import { VideoOverlay } from 'src/lib/common/ui/VideoOverlay';
import { Offscreen } from 'src/lib/common/ui/Offscreen';
import { VideoPlayer } from 'src/lib/common/ui/VideoPlayer';
import { PlayIcon, ZoomIcon } from 'src/lib/assets/icons';
import { ProductTasteGraphic } from './ProductTasteGraphic';
import styles from './ProductTile.module.scss';

interface ProductTileProps {
  product: Product,
}

export function ProductTile(props: ProductTileProps) {
  const { t } = useTranslation();

  return (
    <article className={ styles.container } data-tracking-role="product-tile" data-tracking-id={ props.product.id }>

      { props.product.tasteGraph &&
        (
          props.product.tasteGraph.flavour.length > 0 ||
          props.product.tasteGraph.mouthfeel.length > 0 ||
          props.product.tasteGraph.outer_flavour.length > 0 ||
          props.product.tasteGraph.taste.length > 0 ||
          props.product.tasteGraph.taste_dimension.length > 0
        ) && <ProductGraphBlock product={props.product} />
      }

      <div className={ styles.contentBlock }>

        <h2 className={ styles.title }>
          { props.product.title }
        </h2>

        { (props.product.prominentFlavors.length > 0 || props.product.tasteVideoLandscape || props.product.tasteVideoPortrait) &&
          <div className={ styles.contentMain }>

            { props.product.prominentFlavors.length > 0 &&
              <div className={ styles.contentSection }>
                <h3 className={ styles.contentSectionTitle }>
                  { t('results_flavours') }:
                </h3>
                <div className={ styles.contentSectionContent }>
                  <FlavourList flavours={ props.product.prominentFlavors } />
                </div>
              </div>
            }

            { (props.product.tasteVideoLandscape || props.product.tasteVideoPortrait) &&
              <div className={ styles.contentSection }>
                <Offscreen as='h3'>
                  { t('results_video') }:
                </Offscreen>
                <div className={ styles.contentSectionContent }>
                  <Video product={ props.product } />
                </div>
              </div>
            }

          </div>
        }

      </div>

      <div className={ styles.actionBlock }>
        <a href={ props.product.url } target="_blank" rel="noopener noreferrer" className={ styles.actionLink } data-tracking-role="product-detail-link" data-tracking-id={ props.product.id }>
          { t('results_product_link') }
        </a>
      </div>

    </article>
  );
}

interface ProductGraphBlockProps {
  product: Product,
}

function ProductGraphBlock(props: ProductGraphBlockProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className={ styles.graphBlock }>

      <div className={ styles.graphHolder }>
        <Offscreen>
          { t('results_tasteprofile_title') }
        </Offscreen>
        <ProductTasteGraphic product={ props.product } displayMode='preview' />
      </div>

      <button type="button" onClick={ () => setModalOpen(true) } className={ styles.zoomTrigger }>
        <Offscreen>
          { t('results_tasteprofile_zoom_trigger') }: { props.product.title }
        </Offscreen>
        <span className={ styles.zoomTriggerIconHolder }>
          <ZoomIcon />
        </span>
      </button>

      <ZoomOverlay open={ modalOpen } setOpen={ setModalOpen }>
        <ProductTasteGraphic product={ props.product } displayMode='full' />
      </ZoomOverlay>

    </div>
  );
}

interface FlavourListProps {
  flavours: Flavour[],
}

function FlavourList(props: FlavourListProps) {
  return (
    <ul className={ styles.flavourList }>
      {
        props.flavours.map(flavour => (
          <li key={ flavour.id } className={ styles.flavourListItem }>
            <FlavourPill flavour={ flavour } />
          </li>
        ))
      }
    </ul>
  );
}

interface FlavourPillProps {
  flavour: Flavour,
}

function FlavourPill(props: FlavourPillProps) {
  return (
    <div className={ styles.flavourPill } data-type={ props.flavour.id }>
      { props.flavour.title }
    </div>
  );
}

interface VideoProps {
  product: Product,
}

function Video(props: VideoProps) {
  const { t } = useTranslation();
  const [modalLandscapeOpen, setModalLandscapeOpen] = useState(false);
  const [modalPortraitOpen, setModalPortraitOpen] = useState(false);
  const hasMultipleVideos = props.product.tasteVideoLandscape && props.product.tasteVideoPortrait;

  return (
    <>
      { (props.product.tasteVideoLandscape || props.product.tasteVideoPortrait ) && 
        <div className={ styles.videoBlock } data-multiple={ hasMultipleVideos ? 'true' : 'false' }>
          <div className={ styles.videoBlockInner }>

            <div className={ styles.videoPoster }>
              { props.product.tasteVideoLandscape?.image &&
                <img src={ props.product.tasteVideoLandscape?.image } alt="" loading="lazy" />
              }
              <span className={ styles.videoPosterIcon }>
                <PlayIcon />
              </span>
            </div>

            <div className={ styles.videoText }>
              { props.product.tasteVideoLandscape &&
                <button type="button" onClick={ () => setModalLandscapeOpen(true) } className={ styles.videoButton } data-type="landscape" data-tracking-role="product-video-trigger" data-tracking-id={ props.product.id }>
                  { t('results_video_button') }
                </button>
              }
              { props.product.tasteVideoPortrait &&
                <button type="button" onClick={ () => setModalPortraitOpen(true) } className={ styles.videoButton } data-type="portrait" data-tracking-role="product-video-trigger" data-tracking-id={ props.product.id }>
                  { t('results_video_button') }
                </button>
              }
            </div>

            { props.product.tasteVideoLandscape &&
              <VideoOverlay open={ modalLandscapeOpen } setOpen={ setModalLandscapeOpen }>
                <VideoPlayer url={ props.product.tasteVideoLandscape?.url } posterUrl={ props.product.tasteVideoLandscape?.image } autoplay={ true } type="landscape" />
              </VideoOverlay>
            }

            { props.product.tasteVideoPortrait && 
              <VideoOverlay open={ modalPortraitOpen } setOpen={ setModalPortraitOpen }>
                <VideoPlayer url={ props.product.tasteVideoPortrait?.url } posterUrl={ props.product.tasteVideoPortrait?.image } autoplay={ true } type="portrait" />
              </VideoOverlay>
            }

          </div>
        </div>
      }
    </>
  );
}
